/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import type IntlService from 'embercom/services/intl';
import { inject as service } from '@ember/service';
import { REVIEWABLE_CONTENT_ENTITIES } from 'embercom/lib/ai-content-library/constants';
import {
  objectNames,
  objectNamesToKeys,
} from 'embercom/models/data/matching-system/matching-constants';
import type Transition from '@ember/routing/transition';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import { EntityType } from 'embercom/models/data/entity-types';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type FinContentReviewsController from 'embercom/controllers/apps/app/automation/fin-ai-agent/content-suggestions';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import { EditorMode } from 'embercom/components/knowledge-hub/content-editor/drawer-editor';
import type OptimizeSuggestionsService from 'embercom/services/optimize-suggestions-service';
import { taskFor } from 'ember-concurrency-ts';

export default class OptimizeSuggestionsRoute extends Route {
  analytics = {
    section: 'automation',
    place: 'fin-ai-agent.analyze.optimize-suggestions',
  };

  routeWhenNotOptedIn = 'apps.app.automation.fin-ai-agent.analyze.performance';

  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare optimizeSuggestionsService: OptimizeSuggestionsService;
  @service declare permissionsService: $TSFixMe;

  beforeModel(transition: Transition) {
    this.permissionsService.ensurePermissionWhenTransitioning('can_access_reporting', transition);

    // Redirect standalone users to their route
    if (this.appService.app.isStandaloneApp) {
      transition.abort();
      this.router.transitionTo('apps.app.standalone.optimize-suggestions');
      return;
    }

    if (!this.appService.app.canSeeOptimize) {
      this.router.transitionTo(this.routeWhenNotOptedIn);
      return;
    }
  }

  async model(): Promise<void> {
    await taskFor(this.optimizeSuggestionsService.loadSuggestions).perform();
  }

  setupController(
    controller: $TSFixMe,
    model: ModelFromRoute<OptimizeSuggestionsRoute>,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    let queryParams = transition.to.queryParams;
    let contentType = queryParams.content;

    let firstSuggestion = this.optimizeSuggestionsService.suggestions.firstObject;
    let firstContentId = firstSuggestion?.entityId;
    let firstContentType = firstSuggestion?.contentType;
    if (firstSuggestion && !queryParams.activeContentId && firstContentId) {
      //unregister any stale active content models
      this.knowledgeHubEditorService.registerActiveContent(undefined);

      controller.setProperties({
        activeContentId: firstContentId,
        activeContentType: firstContentType ? objectNames[firstContentType] || '' : '',
        editorMode: EditorMode.REVIEW,
      });

      //ensure that the url is always updated correctly
      this.router.transitionTo({
        queryParams: {
          activeContentId: firstContentId,
          activeContentType: firstContentType ? objectNames[firstContentType] || '' : '',
          editorMode: EditorMode.REVIEW,
        },
      });

      return;
    }

    if (!contentType) {
      return;
    }
    if (!this.reviewableContentEntities.includes(Number(objectNamesToKeys[contentType]))) {
      controller.setProperties({
        selectedContentType: undefined,
        selectedContentId: undefined,
      });
      this.redirectToFinContent(transition);
    }
  }

  deactivate() {
    this.contentSuggestionsService.listApi?.reset();
  }

  private redirectToFinContent(transition: Transition): void {
    this.router.transitionTo({
      queryParams: {
        ...transition.to.queryParams,
        selectedContentType: undefined,
        selectedContentId: undefined,
        content: undefined,
        id: undefined,
      },
    });
  }

  private get reviewableContentEntities(): EntityType[] {
    let reviewableContentEntities = [...REVIEWABLE_CONTENT_ENTITIES];
    reviewableContentEntities.push(EntityType.ArticleContent);
    reviewableContentEntities.push(EntityType.ContentSectionRelationship);
    return reviewableContentEntities;
  }

  get titleToken(): string {
    return this.intl.t('ai-agent.sub-menu.content-suggestions.title');
  }

  resetController(controller: FinContentReviewsController, isExiting: boolean) {
    if (isExiting) {
      this.knowledgeHubDrawerEditorService.removeEmberStickyQueryParams(controller);
    }
  }
}
